import { API_PATH, BASE_PATH } from '../config';
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  Stack
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import AIIcon from './AIIcon';
import { useTranslation } from 'react-i18next';
import { getActivityDisplayTitle } from '../utils/activityUtils';

const AIAssistant = ({ open, onClose, tripId, tripData = null, contextType = 'general', contextId = null, contextActivity = null, onOpenAddFlightModal, onOpenAddActivityModal, onOpenEditActivityModal }) => {
  const { i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [executingActions, setExecutingActions] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [currentContext, setCurrentContext] = useState({ type: contextType, id: contextId });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reload history when context changes
  useEffect(() => {
    if (open && (contextType !== currentContext.type || contextId !== currentContext.id)) {
      setCurrentContext({ type: contextType, id: contextId });
      setHistoryLoaded(false);
      setMessages([]);
    }
  }, [open, contextType, contextId]);

  // Load conversation history when drawer opens
  useEffect(() => {
    if (open && tripId && !historyLoaded) {
      loadConversationHistory();
    }
  }, [open, tripId, historyLoaded]);

  const loadConversationHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_PATH}/ai/get_history.php?trip_id=${tripId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      // Add greeting message after history
      const greetingMessage = getGreetingMessage();
      
      if (data.success && data.history && data.history.length > 0) {
        const historyMessages = [];
        data.history.forEach(entry => {
          historyMessages.push({
            role: 'user',
            content: entry.user_message,
            timestamp: new Date(entry.timestamp),
            isHistory: true
          });
          historyMessages.push({
            role: 'assistant',
            content: entry.ai_response,
            actions: entry.actions || [],
            has_actions: (entry.actions && entry.actions.length > 0),
            timestamp: new Date(entry.timestamp),
            isHistory: true
          });
        });
        // Place greeting after history
        const finalMessages = greetingMessage ? [...historyMessages, greetingMessage] : historyMessages;
        setMessages(finalMessages);
      } else {
        // No history, just show greeting
        setMessages(greetingMessage ? [greetingMessage] : []);
      }
      setHistoryLoaded(true);
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      const greetingMessage = getGreetingMessage();
      if (greetingMessage) {
        setMessages([greetingMessage]);
      }
      setHistoryLoaded(true);
    }
  };

  const getGreetingMessage = () => {
    let greeting = '';
    let scopeNote = '';
    const destination = tripData?.start_location || 'your destination';
    
    if (contextType === 'general') {
      greeting = `Let's work on your trip to ${destination}. What would you like to do?`;
      scopeNote = '🔓 Full trip access: I can add, modify, or remove activities on any day.';
    } else if (contextType === 'day') {
      greeting = `Let's work on Day ${contextId} of your trip. What would you like to do?`;
      scopeNote = `🔒 Day ${contextId} focus: I can add, modify, or remove activities on this day only.`;
    } else if (contextType === 'activity' && contextActivity) {
      const activityTitle = getActivityDisplayTitle(contextActivity) || 'this activity';
      const dayNum = contextActivity.day || '?';
      greeting = `Let's work on "${activityTitle}" on Day ${dayNum}. What would you like to do?`;
      scopeNote = '🔒 Activity focus: I can only modify this specific activity (time, duration, location, etc.).';
    }

    if (greeting) {
      return {
        role: 'system-greeting',
        content: greeting + (scopeNote ? `\n\n${scopeNote}` : ''),
        timestamp: new Date(),
        isHistory: false
      };
    }
    return null;
  };

  const handleSendMessage = async (directMessage = null) => {
    // Safety check: if directMessage is an event object, treat it as null
    const messageContent = (directMessage && typeof directMessage === 'object' && directMessage.nativeEvent) 
      ? null 
      : directMessage;

    const messageToSend = messageContent || inputMessage.trim();
    
    if (!messageToSend || loading) return;

    // Clear input only if not using direct message
    if (!messageContent) {
      setInputMessage('');
    }
    
    // Add user message to chat
    setMessages(prev => [...prev, {
      role: 'user',
      content: messageToSend,
      timestamp: new Date()
    }]);

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessages(prev => [...prev, {
          role: 'error',
          content: 'Authentication token not found. Please log in.',
          timestamp: new Date()
        }]);
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_PATH}/ai/chat.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripId,
          message: messageToSend,
          context_type: contextType,
          context_id: contextId,
          language: i18n.language
        })
      });

      const data = await response.json();

      if (data.success) {
        // Clean up AI response - remove ACTION BLOCKS
        let cleanResponse = cleanAIResponse(data.response);
        
        // If there are actions, append formatted summary
        let displayContent = cleanResponse;
        if (data.has_actions && data.actions && data.actions.length > 0) {
          const actionSummary = formatActionsForDisplay(data.actions);
          if (actionSummary) {
            displayContent = cleanResponse + '\n\n' + actionSummary;
          }
        }
        
        // Add AI response to chat
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: displayContent,
          actions: data.actions,
          has_actions: data.has_actions,
          timestamp: new Date()
        }]);
        
        // Focus input after AI response
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
        // Return the raw response data to callers (useful when called programmatically)
        return data;
      } else {
        setMessages(prev => [...prev, {
          role: 'error',
          content: data.message || 'Failed to get AI response',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'error',
        content: 'Network error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteActions = async (actions) => {
    // Handle AUTO_POPULATE_TRIP first (triggers the full generation job)
    const autoPopulate = actions.find(a => a.type === 'auto_populate_trip');
    if (autoPopulate) {
      setExecutingActions(true);
      setMessages(prev => [...prev, {
        role: 'system',
        content: '⚙️ Starting full itinerary generation... This may take a minute.',
        timestamp: new Date()
      }]);
      try {
        const token = localStorage.getItem('token');
        const jobRes = await fetch(`${API_PATH}/trips/start_generation_job.php?trip_id=${tripId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ trip_id: tripId })
        });
        if (jobRes.ok) {
          setMessages(prev => [...prev, {
            role: 'system',
            content: '✅ Generation job started! The page will refresh once complete.',
            timestamp: new Date()
          }]);
          setTimeout(() => { window.location.reload(); }, 2000);
        } else {
          setMessages(prev => [...prev, {
            role: 'error',
            content: 'Failed to start generation job. Please try using the Generate button on the trip page.',
            timestamp: new Date()
          }]);
        }
      } catch (e) {
        setMessages(prev => [...prev, {
          role: 'error',
          content: 'Network error while starting generation.',
          timestamp: new Date()
        }]);
      } finally {
        setExecutingActions(false);
      }
      return;
    }

    // First, handle any modal-opening actions
    const modalActions = actions.filter(a => 
      a.type === 'open_add_flight_modal' || 
      a.type === 'open_add_activity_modal' || 
      a.type === 'open_edit_activity_modal'
    );
    
    const dbActions = actions.filter(a =>
      a.type === 'add_activity' ||
      a.type === 'modify_activity' ||
      a.type === 'delete_activity' ||
      a.type === 'change_location' ||
      a.type === 'log_flight' ||
      a.type === 'manage_hotel' ||
      a.type === 'swap_days' ||
      a.type === 'swap_cities' ||
      a.type === 'undo_last_action'
      // auto_populate_trip is handled separately above
    );

    // Execute modal actions immediately (open UI)
    for (const action of modalActions) {
      if (action.type === 'open_add_flight_modal' && onOpenAddFlightModal) {
        onOpenAddFlightModal(action.day);
        setMessages(prev => [...prev, {
          role: 'system',
          content: `✅ Opening flight entry form${action.day ? ` for Day ${action.day}` : ''}`,
          timestamp: new Date()
        }]);
        onClose(); // Close AI drawer when opening modal
        return; // Don't execute DB actions yet
      } else if (action.type === 'open_add_activity_modal' && onOpenAddActivityModal) {
        onOpenAddActivityModal(action.day);
        setMessages(prev => [...prev, {
          role: 'system',
          content: `✅ Opening activity entry form${action.day ? ` for Day ${action.day}` : ''}`,
          timestamp: new Date()
        }]);
        onClose();
        return;
      } else if (action.type === 'open_edit_activity_modal' && onOpenEditActivityModal) {
        onOpenEditActivityModal(action.activity_id);
        setMessages(prev => [...prev, {
          role: 'system',
          content: `✅ Opening edit form for activity`,
          timestamp: new Date()
        }]);
        onClose();
        return;
      }
    }

    // Only execute DB actions if there are no modal actions
    if (dbActions.length === 0) {
      return;
    }

    setExecutingActions(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessages(prev => [...prev, {
          role: 'error',
          content: 'Authentication token not found. Please log in.',
          timestamp: new Date()
        }]);
        setExecutingActions(false);
        return;
      }

      const response = await fetch(`${API_PATH}/ai/execute_actions.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trip_id: tripId,
          actions: dbActions
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessages(prev => [...prev, {
          role: 'system',
          content: `✅ Successfully executed ${data.results.length} action(s):\n${data.results.map(r => r.message).join('\n')}`,
          timestamp: new Date()
        }]);
        
        // Refresh the page to show updated activities
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setMessages(prev => [...prev, {
          role: 'error',
          content: 'Failed to execute actions: ' + data.message,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'error',
        content: 'Network error while executing actions.',
        timestamp: new Date()
      }]);
    } finally {
      setExecutingActions(false);
    }
  };

  // Enter sends the message; Shift+Enter adds a new line

  const handleClose = () => {
    setHistoryLoaded(false); // Reset flag when closing
    onClose();
  };

  // Start a fresh chat session: wipe server-side history then reload the greeting.
  const handleNewSession = async () => {
    if (loading || executingActions) return;
    try {
      const token = localStorage.getItem('token');
      if (token && tripId) {
        await fetch(`${API_PATH}/ai/clear_history.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ trip_id: tripId })
        });
      }
    } catch (e) {
      console.error('Failed to clear chat history:', e);
    } finally {
      // Clear the view and force history (now empty) + greeting to reload.
      setInputMessage('');
      setMessages([]);
      setHistoryLoaded(false);
    }
  };

  const getContextLabel = () => {
    if (contextType === 'activity') return 'Activity Context';
    if (contextType === 'day') return `Day ${contextId} Context`;
    return 'General';
  };

  const isConfirmationMessage = (content) => {
    const lowerContent = content.toLowerCase();
    return lowerContent.includes('execution plan:') || 
           lowerContent.includes('🎯 execution plan');
  };

  const hasMultipleChoiceOptions = (content) => {
    // Check if message contains A), B), C) pattern
    const optionPattern = /[A-C]\)/g;
    const matches = content.match(optionPattern);
    return matches && matches.length >= 2;
  };

  const extractOptions = (content) => {
    // Extract options like "A) Snorkeling...", "B) Jet Ski...", "C) Beach..."
    const lines = content.split('\n');
    const options = [];
    
    for (const line of lines) {
      const match = line.match(/^([A-C])\)\s*(.+)$/);
      if (match) {
        options.push({
          letter: match[1],
          text: match[2].trim()
        });
      }
    }
    
    return options;
  };

  const cleanAIResponse = (response) => {
    // Remove ACTION BLOCKS from display
    let cleaned = response;
    
    // Remove the entire ACTION block including content
    cleaned = cleaned.replace(/\[ACTION:[^\]]+\][\s\S]*?\[\/ACTION\]/g, '');
    
    // Remove "No action yet" messages
    cleaned = cleaned.replace(/\n*ACTION BLOCKS?:\s*\n*No action yet\s*/gi, '');
    cleaned = cleaned.replace(/\n*ACTION BLOCKS?:\s*\n*\(No action yet\)\s*/gi, '');
    
    return cleaned.trim();
  };

  const formatActionsForDisplay = (actions) => {
    if (!actions || actions.length === 0) return '';
    
    // Helper function to find activity by ID
    const findActivityById = (activityId) => {
      if (!tripData?.days) return null;
      
      for (const day of tripData.days) {
        if (day.activities) {
          const activity = day.activities.find(act => act.activity_id == activityId);
          if (activity) return activity;
        }
      }
      return null;
    };
    
    let summary = '📋 Summary of Changes:\n\n';
    
    actions.forEach((action, index) => {
      if (action.type === 'add_activity') {
        summary += `✅ Adding "${action.title || 'New Activity'}"\n`;
        summary += `   📅 Day ${action.day}\n`;
        summary += `   🕐 Time: ${action.start_time || 'TBD'}\n`;
        summary += `   ⏱️ Duration: ${action.duration || 60} minutes\n`;
        if (action.location) summary += `   📍 Location: ${action.location}\n`;
      } else if (action.type === 'modify_activity') {
        const fieldNames = {
          'day': 'Day',
          'title': 'Title',
          'start_time': 'Start Time',
          'duration': 'Duration',
          'location': 'Location',
          'notes': 'Notes'
        };
        
        // Look up activity name
        const activity = findActivityById(action.activity_id);
        const activityName = activity ? getActivityDisplayTitle(activity) : `Activity #${action.activity_id}`;
        
        summary += `✏️ Modifying "${activityName}"\n`;
        summary += `   Changing ${fieldNames[action.field] || action.field} to: ${action.value}\n`;
      } else if (action.type === 'change_location') {
        summary += `📍 Changing Day ${action.day} location to: ${action.location}\n`;
      } else if (action.type === 'log_flight') {
        summary += `✈️ Logging flight: ${action.operator || ''} ${action.flight_number || ''}\n`;
        if (action.date) summary += `   📅 Date: ${action.date}\n`;
        if (action.origin && action.destination) summary += `   🛫 ${action.origin} → ${action.destination}\n`;
        if (action.confirmation_number) summary += `   🔖 Conf: ${action.confirmation_number}\n`;
      } else if (action.type === 'manage_hotel') {
        summary += `🏨 Saving hotel: ${action.hotel_name || 'Hotel'}\n`;
        if (action.check_in_date) summary += `   📅 Check-in: ${action.check_in_date}\n`;
        if (action.check_out_date) summary += `   📅 Check-out: ${action.check_out_date}\n`;
        if (action.conf_number) summary += `   🔖 Ref: ${action.conf_number}\n`;
      } else if (action.type === 'swap_days') {
        summary += `🔄 Swapping Day ${action.day_a} ↔ Day ${action.day_b} (all activities and city)\n`;
      } else if (action.type === 'swap_cities') {
        summary += `🔄 Swapping "${action.city_a}" ↔ "${action.city_b}" (all their days and activities)\n`;
      } else if (action.type === 'undo_last_action') {
        summary += `↩️ Undoing last action\n`;
      } else if (action.type === 'delete_activity') {
        // Look up activity name for deletion
        const activity = findActivityById(action.activity_id);
        const activityName = activity ? getActivityDisplayTitle(activity) : `Activity #${action.activity_id}`;
        
        summary += `🗑️ Deleting "${activityName}"\n`;
      }
      
      if (index < actions.length - 1) summary += '\n';
    });
    
    return summary;
  };

  const handleQuickReply = async (reply) => {
    if (reply === 'Execute') {
      // Clear input and attempt to execute any already-present actions.
      setInputMessage('');

      // Find the last AI message with actions
      const lastAIMessage = [...messages].reverse().find(m => 
        m.role === 'assistant' && m.has_actions && m.actions && m.actions.length > 0
      );

      if (lastAIMessage && lastAIMessage.actions && lastAIMessage.actions.length > 0) {
        // Execute the actions directly
        await handleExecuteActions(lastAIMessage.actions);
        return;
      }

      // No actions present yet: send 'Execute' to the backend so the assistant can emit the ACTION block
      setLoading(true);
      try {
        const data = await handleSendMessage('Execute');
        // If assistant returned actions in response, execute them
        if (data && data.success && data.has_actions && data.actions && data.actions.length > 0) {
          await handleExecuteActions(data.actions);
          return;
        }

        // If still no actions, show clear error to user
        setMessages(prev => [...prev, {
          role: 'error',
          content: 'No actions were produced by the assistant after confirmation.',
          timestamp: new Date()
        }]);
      } catch (e) {
        setMessages(prev => [...prev, {
          role: 'error',
          content: 'Failed to confirm. Please try again.',
          timestamp: new Date()
        }]);
      } finally {
        setLoading(false);
      }
    } else if (reply === 'Cancel') {
      // For Cancel, send directly
      handleSendMessage(reply);
    } else {
      // For other quick replies
      setInputMessage(reply);
      setTimeout(() => handleSendMessage(), 100);
    }
  };

  // Called when user clicks a multiple-choice option button.
  // Sends the selection immediately and auto-executes any actions the AI emits.
  const handleOptionSelect = async (optionText) => {
    if (loading || executingActions) return;
    const data = await handleSendMessage(optionText);
    if (data && data.success && data.has_actions && data.actions && data.actions.length > 0) {
      await handleExecuteActions(data.actions);
    }
  };

  if (!open) return null;

  return (
    <Box
      className="ai-assistant-fullscreen"
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        bgcolor: '#0d1117',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ height: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(99,102,241,0.25)',
          bgcolor: '#161b22',
          color: 'rgba(255,255,255,0.92)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AIIcon sx={{ color: '#818cf8' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.95)', lineHeight: 1.2 }}>AI Assistant</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.03em' }}>
                {getContextLabel()}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="New session — forget this chat history">
              <IconButton
                onClick={handleNewSession}
                disabled={loading || executingActions}
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  bgcolor: 'rgba(255,255,255,0.07)',
                  '&:hover': {
                    color: '#a5b4fc',
                    bgcolor: 'rgba(99,102,241,0.18)'
                  },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' }
                }}
                size="medium"
                aria-label="Start new chat session"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Back to itinerary">
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  bgcolor: 'rgba(255,255,255,0.07)',
                  '&:hover': {
                    color: 'rgba(255,255,255,0.9)',
                    bgcolor: 'rgba(255,255,255,0.12)'
                  }
                }}
                size="medium"
                aria-label="Back to itinerary"
              >
                <FormatListBulletedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Messages */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          bgcolor: '#0d1117'
        }}>
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4, color: 'rgba(255,255,255,0.35)' }}>
              <AIIcon sx={{ fontSize: 64, opacity: 0.25, mb: 2, color: '#818cf8' }} />
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Ask me anything about your trip!
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.3)' }}>
                I can help you add, modify, or remove activities.
              </Typography>
            </Box>
          )}

          {messages.map((message, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {message.role === 'user' && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Paper sx={{ 
                    p: 1.5, 
                    maxWidth: '80%',
                    bgcolor: 'rgba(99,102,241,0.18)',
                    border: '1px solid rgba(99,102,241,0.3)',
                    boxShadow: 'none',
                    color: 'rgba(255,255,255,0.9)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 16, color: '#a5b4fc' }} />
                      <Typography variant="caption" sx={{ color: '#a5b4fc', fontWeight: 600 }}>You</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', direction: 'auto', unicodeBidi: 'plaintext', textAlign: 'start', color: 'rgba(255,255,255,0.9)' }}>
                      {message.content}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {message.role === 'assistant' && (
                <Box>
                  <Paper sx={{ 
                    p: 1.5, 
                    maxWidth: '80%',
                    bgcolor: message.isHistory ? '#1a1f27' : '#21262d',
                    border: `1px solid ${message.isHistory ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.18)'}`,
                    boxShadow: 'none',
                    opacity: message.isHistory ? 0.7 : 1
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <AIIcon sx={{ fontSize: 16, color: '#818cf8' }} />
                      <Typography variant="caption" sx={{ color: '#818cf8', fontWeight: 600 }}>
                        AI Assistant {message.isHistory && '(Previous)'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', direction: 'auto', unicodeBidi: 'plaintext', textAlign: 'start', color: 'rgba(255,255,255,0.85)' }}>
                      {message.content}
                    </Typography>

                    {/* Quick Reply Buttons for Confirmation - Only for non-history messages */}
                    {!message.isHistory && isConfirmationMessage(message.content) && !message.has_actions && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleQuickReply('Execute')}
                          disabled={loading}
                          sx={{ flex: 1, bgcolor: 'rgba(34,197,94,0.2)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.35)', '&:hover': { bgcolor: 'rgba(34,197,94,0.3)' }, boxShadow: 'none' }}
                        >
                          ✓ Execute
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleQuickReply('Cancel')}
                          disabled={loading}
                          sx={{ flex: 1, color: '#f87171', borderColor: 'rgba(248,113,113,0.4)', '&:hover': { bgcolor: 'rgba(248,113,113,0.1)', borderColor: 'rgba(248,113,113,0.6)' } }}
                        >
                          ✕ Cancel
                        </Button>
                      </Stack>
                    )}

                    {/* Multiple Choice Option Buttons - Only for non-history messages */}
                    {!message.isHistory && hasMultipleChoiceOptions(message.content) && !isConfirmationMessage(message.content) && (
                      <Stack spacing={1} sx={{ mt: 1.5 }}>
                        {extractOptions(message.content).map((option) => (
                          <Button
                            key={option.letter}
                            variant="outlined"
                            size="medium"
                            onClick={() => handleOptionSelect(option.text)}
                            disabled={loading || executingActions}
                            sx={{ 
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              textTransform: 'none',
                              px: 2,
                              py: 1.5,
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                              minHeight: 'auto',
                              color: 'rgba(255,255,255,0.8)',
                              borderColor: 'rgba(99,102,241,0.35)',
                              '&:hover': { bgcolor: 'rgba(99,102,241,0.12)', borderColor: 'rgba(99,102,241,0.6)' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1.5, minWidth: '24px', flexShrink: 0, color: '#818cf8' }}>
                                {option.letter})
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                flex: 1,
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                color: 'rgba(255,255,255,0.8)'
                              }}>
                                {option.text}
                              </Typography>
                            </Box>
                          </Button>
                        ))}
                      </Stack>
                    )}

                    {/* Only show action buttons for non-history messages */}
                    {!message.isHistory && message.has_actions && message.actions && message.actions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.08)' }} />
                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'rgba(255,255,255,0.4)' }}>
                          Suggested Actions:
                        </Typography>
                        <Stack spacing={0.5}>
                          {message.actions.map((action, idx) => (
                            <Chip
                              key={idx}
                              label={
                                action.type === 'swap_days'
                                  ? `swap_days: Day ${action.day_a} ↔ Day ${action.day_b}`
                                  : action.type === 'swap_cities'
                                  ? `swap_cities: ${action.city_a} ↔ ${action.city_b}`
                                  : `${action.type}: ${action.title || action.field || action.activity_id}`
                              }
                              size="small"
                              variant="outlined"
                              sx={{ color: '#818cf8', borderColor: 'rgba(99,102,241,0.4)', bgcolor: 'rgba(99,102,241,0.1)' }}
                            />
                          ))}
                        </Stack>
                        <Button
                          fullWidth
                          variant="contained"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => { e.stopPropagation(); handleExecuteActions(message.actions); }}
                          disabled={executingActions}
                          sx={{ mt: 1.5, bgcolor: '#6366f1', color: 'white', '&:hover': { bgcolor: '#4f46e5' }, '&:disabled': { bgcolor: 'rgba(99,102,241,0.3)', color: 'rgba(255,255,255,0.4)' } }}
                        >
                          {executingActions ? 'Executing...' : 'Apply Changes'}
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {message.role === 'system' && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {message.content}
                </Alert>
              )}

              {message.role === 'system-greeting' && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  mb: 2,
                  mt: 1
                }}>
                  <Paper sx={{ 
                    p: 2.5,
                    maxWidth: '85%',
                    textAlign: 'center',
                    bgcolor: 'rgba(99,102,241,0.1)',
                    border: '1px solid rgba(99,102,241,0.3)',
                    borderRadius: '16px',
                    boxShadow: '0 2px 16px rgba(99,102,241,0.12)'
                  }}>
                    <Typography variant="body1" sx={{ 
                      color: 'rgba(255,255,255,0.88)',
                      fontWeight: 500,
                      lineHeight: 1.6
                    }}>
                      {message.content}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {message.role === 'error' && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {message.content}
                </Alert>
              )}
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1 }}>
              <CircularProgress size={18} sx={{ color: '#818cf8' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
                AI is thinking...
              </Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid rgba(99,102,241,0.2)',
          bgcolor: '#161b22'
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={3}
              placeholder="Ask me to add, change, or remove activities..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={loading || executingActions}
              size="small"
              inputRef={inputRef}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#0d1117',
                  color: 'rgba(255,255,255,0.88)',
                  '& fieldset': { borderColor: 'rgba(99,102,241,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.55)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                  '&.Mui-disabled': { bgcolor: 'rgba(13,17,23,0.6)' },
                },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
                '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(255,255,255,0.25)' },
              }}
            />
            <IconButton
              type="button"
              color="primary"
              onMouseDown={(e) => e.preventDefault()} // Prevent TextField from losing focus
              onClick={(e) => { e.stopPropagation(); handleSendMessage(); }}
              disabled={!inputMessage.trim() || loading || executingActions}
              sx={{ 
                bgcolor: '#6366f1',
                color: 'white',
                '&:hover': { bgcolor: '#4f46e5' },
                '&:disabled': { bgcolor: 'rgba(99,102,241,0.2)', color: 'rgba(255,255,255,0.3)' }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255,255,255,0.3)' }}>
            Example: "Add a visit to the Acropolis on day 2" or "Move dinner to 7pm"
          </Typography>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{ mt: 2, color: 'rgba(255,255,255,0.55)', borderColor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.3)' } }}
            startIcon={<FormatListBulletedIcon />}
          >
            Back to Itinerary
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AIAssistant;
